import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import { DeviceEnrichment } from '../types';
import {
  ENRICHMENT_NEEDS_READ_DEVICES,
  enrichmentErrorStatus,
  subscribeToEnrichmentRefresh,
} from './useFirestoreEnrichment';

interface DeviceEnrichmentState {
  /** The device this state was read for, so a switch never shows the last one's records. */
  deviceName: string;
  enrichment: DeviceEnrichment | null;
  loading: boolean;
  error: string | null;
  /** firebase-api refused the read (403), so the panel can say why. */
  refused?: boolean;
}

/**
 * One device's app records, read on their own (FS-539).
 *
 * The batch map behind `useFirestoreEnrichment` covers every device, so it can
 * only afford the records a device points at now. This reads the single-device
 * endpoint, which also returns the device's earlier filter records
 * (`previousFilters`) — the photos that otherwise drop out of view after a
 * filter change. It reads again on the same refresh signal the batch readers
 * listen to, so a change recorded elsewhere on the page reaches it too.
 *
 * A device with no app registration is answered with a 404, which is not an
 * error here: the batch map has no entry for such a device either, and the
 * panels already say so.
 *
 * FS-891: the read needs read_devices. Without it nothing is asked for; a
 * refusal (401/403), or a change of account or of that permission, takes
 * away what was read before. A transient failure still keeps it.
 */
export function useDeviceEnrichment(deviceName: string) {
  const { id: accountId, hasPermission } = useAuth();
  const canRead = hasPermission('read_devices');
  const [state, setState] = useState<DeviceEnrichmentState>({
    deviceName,
    enrichment: null,
    loading: canRead,
    error: null,
  });
  /** Only the newest request may land: a slow answer for an older one is dropped. */
  const latest = useRef(0);

  const fetchEnrichment = useCallback(
    async (quiet = false) => {
      const ticket = ++latest.current;
      if (!canRead) {
        setState({ deviceName, enrichment: null, loading: false, error: null });
        return;
      }
      if (!quiet) setState({ deviceName, enrichment: null, loading: true, error: null });
      try {
        const response = await axiosInstance.get<DeviceEnrichment>(
          `${config.firebaseApiUrl}/fb/devices/enrich/${encodeURIComponent(deviceName)}`,
        );
        if (ticket !== latest.current) return;
        setState({ deviceName, enrichment: response.data, loading: false, error: null });
      } catch (err) {
        if (ticket !== latest.current) return;
        const status = enrichmentErrorStatus(err);
        if (status === 404) {
          setState({ deviceName, enrichment: null, loading: false, error: null });
          return;
        }
        console.error('Error fetching Firestore enrichment for one device:', err);
        if (status === 401 || status === 403) {
          setState({
            deviceName,
            enrichment: null,
            loading: false,
            error: status === 403 ? null : 'Failed to fetch Firestore data for this device',
            refused: status === 403,
          });
          return;
        }
        // Keep what is on screen for this device, as the batch reader does.
        setState((prev) => ({
          deviceName,
          enrichment: prev.deviceName === deviceName ? prev.enrichment : null,
          loading: false,
          error: 'Failed to fetch Firestore data for this device',
        }));
      }
    },
    [deviceName, canRead],
  );

  // A new account starts over too: its first read is not quiet, so nothing
  // read under the old one stays on screen while it runs.
  useEffect(() => {
    fetchEnrichment();
  }, [fetchEnrichment, accountId]);

  useEffect(() => subscribeToEnrichmentRefresh(() => void fetchEnrichment(true)), [fetchEnrichment]);

  // An answer still in flight when the reader goes away is dropped.
  useEffect(
    () => () => {
      latest.current += 1;
    },
    [],
  );

  const current = state.deviceName === deviceName;
  return {
    enrichment: current ? state.enrichment : null,
    loading: current ? state.loading : true,
    error: current ? state.error : null,
    unavailableReason: !canRead || (current && state.refused) ? ENRICHMENT_NEEDS_READ_DEVICES : null,
    refresh: () => fetchEnrichment(true),
  };
}
